import { useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { s, ExcelIcon } from '../listPageStyles';
import '../CustomersPage.css'; // cd-import-* CSS sınıfları paylaşılır

function UploadIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

/**
 * 4 aşamalı Excel/CSV içe aktarım sihirbazı (Müşteri/Fiyat/diğer master sayfaları için).
 * Akış: 1) Dosya → 2) Önizleme → 3) Yükleme → 4) Sonuç
 *
 * Props (generic — sayfa kendi parser/validator/submit'ini verir):
 *   title          — "Fiyat İçe Aktar" gibi başlık
 *   helpText       — pick aşamasında alttaki bilgi metni (zorunlu alanlar vb.)
 *   prepare(rawRows) → Promise<void>    — opsiyonel; satırları payload'a çevirmeden ÖNCE çağrılır
 *                                         (ör. dosyadaki kodları sunucudan toplu çözümlemek için)
 *   rowToPayload(rawRow) → payload      — Excel satırını backend payload'una çevirir
 *   validateRow(payload) → string|null  — null = geçerli, aksi: hata sebebi
 *   submitBatch(payloads) → { ok, fail, errors }  — opsiyonel, tüm payload'ları tek istekte gönderir
 *   submitRow(payload) → { ok, error?:string }    — submitBatch yoksa satır satır kullanılır
 *   downloadTemplate()                  — "Şablonu İndir" butonuna bağlı callback
 */
export function ImportWizard({
  open,
  onClose,
  onComplete,
  title = 'İçe Aktar',
  helpText,
  prepare,
  rowToPayload,
  validateRow,
  submitBatch,
  submitRow,
  downloadTemplate,
}) {
  const [stage, setStage] = useState('pick');
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [parsedPayloads, setParsedPayloads] = useState([]);
  const [validation, setValidation] = useState({ valid: 0, invalid: 0 });
  const [progress, setProgress] = useState({ current: 0, total: 0, ok: 0, fail: 0 });
  const [errors, setErrors] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setStage('pick'); setFile(null); setRows([]); setParsedPayloads([]);
      setValidation({ valid: 0, invalid: 0 });
      setProgress({ current: 0, total: 0, ok: 0, fail: 0 });
      setErrors([]);
    }
  }, [open]);

  if (!open) return null;

  const handleFile = async (f) => {
    if (!f) return;
    setFile(f);
    try {
      const buf = await f.arrayBuffer();
      // cellDates → tarih hücreleri seri-sayı yerine JS Date olarak gelsin (önizleme okunur olsun)
      const wb = XLSX.read(buf, { type: 'array', cellDates: true });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      // Eşleştirme verisini (ör. kod→id) satırları çevirmeden önce sunucudan yükle.
      if (prepare) {
        setPreparing(true);
        try { await prepare(data); }
        finally { setPreparing(false); }
      }
      const payloads = data.map((row) => rowToPayload(row));
      const v = payloads.reduce((acc, p) => {
        if (validateRow(p)) acc.invalid += 1; else acc.valid += 1;
        return acc;
      }, { valid: 0, invalid: 0 });
      setRows(data);
      setParsedPayloads(payloads);
      setValidation(v);
      setStage('preview');
    } catch (e) {
      setErrors([{ row: 0, message: 'Dosya okunamadı: ' + e.message }]);
      setStage('done');
    }
  };

  const startImport = async () => {
    setStage('importing');
    setProgress({ current: 0, total: parsedPayloads.length, ok: 0, fail: 0 });
    setErrors([]);

    // Zorunlu validasyonu burada tekrar yapıp geçersizleri ayırıyoruz; sadece geçerliler gönderilir.
    const validPayloads = [];
    const initialErrs = [];
    parsedPayloads.forEach((p, i) => {
      const err = validateRow(p);
      if (err) initialErrs.push({ row: i + 2, message: err });
      else validPayloads.push({ payload: p, sourceRow: i + 2 });
    });

    let ok = 0;
    let fail = initialErrs.length;
    const errs = [...initialErrs];

    if (submitBatch) {
      // Batch mod — tek istek
      setProgress({ current: 0, total: validPayloads.length, ok: 0, fail });
      try {
        const result = await submitBatch(validPayloads.map(v => v.payload));
        ok = result?.ok ?? 0;
        fail += result?.fail ?? Math.max(0, validPayloads.length - ok);
        (result?.errors ?? []).forEach((m, i) => errs.push({ row: validPayloads[i]?.sourceRow ?? i + 2, message: String(m) }));
      } catch (e) {
        fail += validPayloads.length;
        errs.push({ row: 0, message: 'Toplu yükleme hatası: ' + (e.message || '') });
      }
      setProgress({ current: validPayloads.length, total: validPayloads.length, ok, fail });
    } else if (submitRow) {
      // Satır satır mod
      for (let i = 0; i < validPayloads.length; i += 1) {
        const { payload, sourceRow } = validPayloads[i];
        try {
          const res = await submitRow(payload);
          if (res?.ok) ok += 1;
          else { fail += 1; errs.push({ row: sourceRow, message: res?.error || 'Bilinmeyen hata' }); }
        } catch (e) {
          fail += 1;
          errs.push({ row: sourceRow, message: e.message || 'Network hatası' });
        }
        setProgress({ current: i + 1, total: validPayloads.length, ok, fail });
        if (errs.length !== errors.length) setErrors([...errs].slice(0, 50));
      }
    }

    setErrors(errs.slice(0, 50));
    setStage('done');
  };

  const handleFinish = () => {
    onComplete?.(progress.ok);
    onClose();
  };

  const previewRows = rows.slice(0, 5);
  const previewKeys = previewRows.length > 0 ? Object.keys(previewRows[0]) : [];
  const pct = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  // Hatalı satırların sebeplerini (alan adıyla birlikte) grupla → "neden atlanıyor" net görünsün.
  const reasonCounts = parsedPayloads.reduce((acc, p) => {
    const r = validateRow(p);
    if (r) acc[r] = (acc[r] || 0) + 1;
    return acc;
  }, {});

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.modal, maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <h3 style={s.modalTitle}>{title}</h3>
          <button type="button" style={s.closeBtn} onClick={onClose}>x</button>
        </div>
        <div style={s.modalBody}>
          <div className="cd-import">
            <div className="cd-import-stepbar">
              <span className={`cd-import-step ${stage === 'pick' ? 'is-active' : 'is-done'}`}>1. Dosya</span>
              <span className="cd-import-step-sep">›</span>
              <span className={`cd-import-step ${stage === 'preview' ? 'is-active' : (stage === 'importing' || stage === 'done' ? 'is-done' : '')}`}>2. Önizleme</span>
              <span className="cd-import-step-sep">›</span>
              <span className={`cd-import-step ${stage === 'importing' ? 'is-active' : (stage === 'done' ? 'is-done' : '')}`}>3. Yükleme</span>
              <span className="cd-import-step-sep">›</span>
              <span className={`cd-import-step ${stage === 'done' ? 'is-active' : ''}`}>4. Sonuç</span>
            </div>

            {stage === 'pick' && (
              <>
                <div
                  className={`cd-import-drop${dragActive ? ' is-active' : ''}`}
                  onClick={() => inputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
                  onDrop={(e) => { e.preventDefault(); setDragActive(false); handleFile(e.dataTransfer.files?.[0]); }}
                >
                  <div className="cd-import-drop-icon"><UploadIcon size={24} /></div>
                  <div className="cd-import-drop-title">Dosyayı buraya sürükleyin ya da tıklayın</div>
                  <div className="cd-import-drop-sub">Desteklenen formatlar: .xlsx, .xls, .csv</div>
                </div>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  style={{ display: 'none' }}
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; handleFile(f); }}
                />
                {downloadTemplate && (
                  <button type="button" className="cd-import-template" onClick={downloadTemplate}>
                    <ExcelIcon size={14} /> Şablonu İndir
                  </button>
                )}
                {preparing && (
                  <div className="cd-import-help" style={{ fontWeight: 700 }}>
                    Eşleştirme verileri yükleniyor, lütfen bekleyin…
                  </div>
                )}
                {helpText && <div className="cd-import-help">{helpText}</div>}
              </>
            )}

            {stage === 'preview' && (
              <>
                <div className="cd-import-summary">
                  <div className="cd-import-stat cd-import-stat--info">
                    <div className="cd-import-stat-label">Toplam Satır</div>
                    <div className="cd-import-stat-value">{rows.length}</div>
                  </div>
                  <div className="cd-import-stat cd-import-stat--ok">
                    <div className="cd-import-stat-label">Geçerli</div>
                    <div className="cd-import-stat-value">{validation.valid}</div>
                  </div>
                  <div className="cd-import-stat cd-import-stat--fail">
                    <div className="cd-import-stat-label">Hatalı</div>
                    <div className="cd-import-stat-value">{validation.invalid}</div>
                  </div>
                </div>
                {validation.invalid > 0 && (
                  <div className="cd-import-warn">
                    <div>{validation.invalid} satır atlanacak — sebepler:</div>
                    <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                      {Object.entries(reasonCounts).map(([reason, count]) => (
                        <li key={reason}><strong>{count}</strong> satır: {reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="cd-import-preview">
                  <table>
                    <thead>
                      <tr>{previewKeys.map((k) => <th key={k}>{k}</th>)}<th>Durum</th></tr>
                    </thead>
                    <tbody>
                      {previewRows.map((r, i) => {
                        const invalid = validateRow(parsedPayloads[i]);
                        return (
                          <tr key={i} className={invalid ? 'is-invalid' : ''}>
                            {previewKeys.map((k) => <td key={k} title={String(r[k] ?? '')}>{String(r[k] ?? '')}</td>)}
                            <td title={invalid || 'Geçerli'} style={{ color: invalid ? '#E53E3E' : '#2F855A', fontWeight: 600, whiteSpace: 'nowrap' }}>
                              {invalid ? `✕ ${invalid}` : '✓ Geçerli'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 700 }}>
                  Dosya: <strong>{file?.name}</strong> · İlk 5 satır önizleniyor
                </div>
              </>
            )}

            {stage === 'importing' && (
              <>
                <div className="cd-import-progress">
                  <div className="cd-import-progress-bar">
                    <div className="cd-import-progress-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="cd-import-progress-text">{progress.current} / {progress.total} · %{pct}</div>
                </div>
                <div className="cd-import-summary">
                  <div className="cd-import-stat cd-import-stat--ok">
                    <div className="cd-import-stat-label">Eklenen</div>
                    <div className="cd-import-stat-value">{progress.ok}</div>
                  </div>
                  <div className="cd-import-stat cd-import-stat--fail">
                    <div className="cd-import-stat-label">Başarısız</div>
                    <div className="cd-import-stat-value">{progress.fail}</div>
                  </div>
                </div>
              </>
            )}

            {stage === 'done' && (
              <>
                <div className="cd-import-summary">
                  <div className="cd-import-stat cd-import-stat--ok">
                    <div className="cd-import-stat-label">Eklendi</div>
                    <div className="cd-import-stat-value">{progress.ok}</div>
                  </div>
                  <div className="cd-import-stat cd-import-stat--fail">
                    <div className="cd-import-stat-label">Başarısız</div>
                    <div className="cd-import-stat-value">{progress.fail}</div>
                  </div>
                  <div className="cd-import-stat cd-import-stat--info">
                    <div className="cd-import-stat-label">Toplam</div>
                    <div className="cd-import-stat-value">{progress.total}</div>
                  </div>
                </div>
                {errors.length > 0 && (
                  <div className="cd-import-errors">
                    <div className="cd-import-errors-title">Hatalar ({errors.length}{errors.length === 50 ? '+' : ''})</div>
                    {errors.map((e, i) => (
                      <div key={i} className="cd-import-error">
                        <strong>Satır {e.row}</strong>
                        <span>{e.message}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div style={s.modalFooter}>
          {stage === 'pick' && (
            <button type="button" style={s.btnSecondary} onClick={onClose}>İptal</button>
          )}
          {stage === 'preview' && (
            <>
              <button type="button" style={s.btnSecondary} onClick={() => setStage('pick')}>Geri</button>
              <button type="button" style={s.btnPrimary} disabled={validation.valid === 0} onClick={startImport}>
                {validation.valid} kayıt yükle
              </button>
            </>
          )}
          {stage === 'importing' && (
            <button type="button" style={s.btnSecondary} disabled>Yükleniyor...</button>
          )}
          {stage === 'done' && (
            <button type="button" style={s.btnPrimary} onClick={handleFinish}>Bitir</button>
          )}
        </div>
      </div>
    </div>
  );
}
